import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { fetchData } from 'services/api/fetchData';
import '../../style/scss/modals.scss';
import '../../style/scss/demoEnvironment.scss';

/*
 * DemoEnvironmentProgressModal.tsx
 * Shows the progress of the demo data creation of a demo environment,
 * opened right after the creation or from the demo environment list.
 * When the creation is done the demo login is shown and the demo
 * environment can be opened directly.
 */

interface DemoEnvironmentStatus {
    status: string;
    progress: number;
    login_email: string | null;
    environment_hash: string | null;
}

interface DemoEnvironmentProgressModalProps {
    demoEnvironmentId: number;
    name: string;
    loginEmail?: string | null;
    onFinished?: () => void;
}

const DemoEnvironmentProgressModal: React.FC<DemoEnvironmentProgressModalProps> = ({
    demoEnvironmentId,
    name,
    loginEmail,
    onFinished
}) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const { handleLogout, handleSwitchEnvironment } = useAuthContext();
    const [status, setStatus] = useState<DemoEnvironmentStatus | null>(null);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Fetch the current status on mount, reveal the modal and start polling
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await fetchStatusFromBackend();
                setStatus(response);

                // Poll the progress while the creation runs
                if (response.status === 'creating') {
                    startPolling();
                }
            } catch (error) {
                console.error('Fetching the demo environment status failed', error);
                setStatus({ status: 'failed', progress: 0, login_email: loginEmail ?? null, environment_hash: null });
            } finally {
                revealModal();
            }
        };
        fetchStatus();

        // Stop polling when the modal unmounts
        return () => stopPolling();
    }, []);

    // Fetch the status of the demo environment
    const fetchStatusFromBackend = async (): Promise<DemoEnvironmentStatus> => {
        return await fetchData({ apiUrl: `get_demo_environment_status_as_superuser/${demoEnvironmentId}`, handleLogout });
    };

    // Stop the polling interval
    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    // Poll the status every one and a half seconds while the creation runs
    const startPolling = () => {
        stopPolling();
        pollingRef.current = setInterval(async () => {
            try {
                const response = await fetchStatusFromBackend();

                // Never let the progress go backwards
                setStatus((previous) => ({
                    ...response,
                    progress: Math.max(previous?.progress ?? 0, response.progress)
                }));

                // Stop polling when the creation is done or failed
                if (response.status !== 'creating') {
                    stopPolling();
                    if (onFinished) onFinished();
                }
            } catch (error) {
                console.error('Fetching the demo environment status failed', error);
            }
        }, 1500);
    };

    // Close the modal and refresh the list behind it
    const handleClose = () => {
        if (onFinished) onFinished();
        closeModal();
    };

    // Open the demo environment as the current superuser or staff user
    const handleOpenEnvironment = () => {
        if (status?.environment_hash) {
            handleSwitchEnvironment(status.environment_hash);
        }
    };

    // Render the progress view of a running creation
    const renderCreating = () => (
        <div className='demo-progress-block'>
            <p className='text-center'>{t('demo_environment.progress.creating_message')}</p>
            <p className='text-center progress-label'>{t('demo_environment.progress.progress_label', { progress: status?.progress ?? 0 })}</p>
            <div className='progress-bar'>
                <div className='progress-bar-fill' style={{ width: `${status?.progress ?? 0}%` }} />
            </div>
            <div className='button-row'>
                <SecondaryButton
                    onClick={handleClose}
                    label='general.close'
                    size='small'
                />
            </div>
        </div>
    );

    // Render the finished view with the demo login
    const renderCreated = () => (
        <div className='demo-progress-block'>
            <p className='text-center'>{t('demo_environment.progress.created_message')}</p>
            <div className='demo-login-details'>
                <div className='demo-login-row'>
                    <span className='demo-login-label'>{t('demo_environment.progress.login_email_label')}</span>
                    <span className='demo-login-value'>{status?.login_email ?? loginEmail}</span>
                </div>
                <div className='demo-login-row'>
                    <span className='demo-login-label'>{t('demo_environment.progress.password_label')}</span>
                    <span className='demo-login-value'>123456</span>
                </div>
            </div>
            <div className='button-row'>
                <SecondaryButton
                    onClick={handleClose}
                    label='general.close'
                    size='small'
                />
                <PrimaryButton
                    onClick={handleOpenEnvironment}
                    label='demo_environment.progress.open_environment_button'
                    size='small'
                    disabled={{ disabled: !status?.environment_hash }}
                />
            </div>
        </div>
    );

    // Render the failed view
    const renderFailed = () => (
        <div className='demo-progress-block'>
            <p className='text-center'>{t('demo_environment.progress.failed_message')}</p>
            <div className='button-row'>
                <SecondaryButton
                    onClick={handleClose}
                    label='general.close'
                    size='small'
                />
            </div>
        </div>
    );

    // Determine the icon of the current state
    const icon = status?.status === 'created' ? faCircleCheck : status?.status === 'failed' ? faTriangleExclamation : faWandMagicSparkles;

    return (
        <div className={`demo-environment-progress-modal ${status?.status ?? ''}`}>
            <div className='status-icon'>
                <FontAwesomeIcon icon={icon} />
            </div>
            <h3 className='header text-center'>
                {t('demo_environment.progress.modal_title', { name })}
            </h3>

            {/* Show the progress, the demo login or the failure */}
            {status && (status.status === 'created' ? renderCreated() : status.status === 'failed' ? renderFailed() : renderCreating())}
        </div>
    );
};

export default DemoEnvironmentProgressModal;
